/**
 * Jovii — PROFILE, FRIENDS AND ROOMS, the state.
 *
 * PLAIN TYPESCRIPT, NOT RUNES, for the same reason `chat/store.ts` and `calls/engine.ts`
 * are: a snapshot plus a Svelte-store-compatible `subscribe` runs under `node --test`
 * with no compiler in the way, so the interesting behaviour — the handle cooldown
 * counting down, an ask that nobody answers, a friend request answered from the other
 * side — is testable without a browser. The components hold the only `$state` there is.
 *
 * ── ONE STORE, THREE SCREENS ───────────────────────────────────────────────────────
 *
 * Profile, Friends and Rooms are one account looked at from three angles, and splitting
 * them would mean three `GET /api/me` calls and three copies of "who am I". There is one
 * store per tab (`social()`), it is created on first use, and it fetches nothing until
 * somebody calls `load()`.
 *
 * ── PRESENCE PUSHES AND POLLING ──────────────────────────────────────────────
 *
 * The shared room WebSocket carries friendPresence pushes. watchPresence() also
 * polls GET /api/friends/presence until actual pushes establish a live connection. The poll is deliberately slow
 * (`PRESENCE_POLL_MS`) and stops dead when the page is hidden, because a backgrounded
 * iOS page happily keeps timers alive and the one thing this project has learned twice
 * is that an unfloored repeat is how you get a frame storm (docs/OPEN-DEFECTS.md #2).
 *
 * ── WHAT IS EXPOSED TO WHOM ────────────────────────────────────────────────────────
 *
 *  - `handle`, `displayName`, `avatar`, `color`: to ANY account behind the invite gate
 *    that searches for them. `/api/friends/search` matches on handle and display name
 *    across every account, not just friends. That is what makes "add me, I'm
 *    amber-otter" work at all, and it is worth knowing before you put your real name in.
 *  - what you are watching (`room` + `title`): FRIENDS ONLY. There is no server query
 *    that takes a userId you are not friends with, and presence is driven off the
 *    accepted-friend list.
 *  - room history: neither this store nor the server records it. The retired browser
 *    writer's old localStorage bytes are left untouched.
 *  - the account key: never rendered, never logged, never in a URL. See `api.ts`.
 */

import type {
  AvatarId,
  FriendPresence,
  PublicProfile,
  RoomInvitation,
  SelfProfile,
  SocialClientMessage,
  UserId
} from './protocol';
import { JOIN_REQUEST_TTL_MS, normaliseHandle } from './protocol';
import * as rest from './api';
import { personName } from './people';
import { inviteError } from './invite-errors';
import type { FriendsResponse, HandleCheck, IncomingRequest, MeResponse, OutgoingRequest } from './api';

/** How often the REST presence fallback asks. The push tick is 2 s; this is not that,
 *  and pretending otherwise would just be a request every two seconds for ever. */
export const PRESENCE_POLL_MS = 12_000;

/** How long an unattributed socket error may still be blamed on the ask we just sent.
 *  See `receive()` — the server does not ack `askToJoin`, so correlation is by time. */
export const ASK_ERROR_WINDOW_MS = 4000;

/* ────────────────────────────────────────────────────────────────────────────
 * Ports — everything with a side effect, so tests can hand it something else
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SocialApiPort {
  me(signal?: AbortSignal): Promise<MeResponse>;
  setProfile(patch: { displayName?: string; avatar?: AvatarId; privateMode?: boolean }, signal?: AbortSignal): Promise<{ me: SelfProfile }>;
  setHandle(handle: string, signal?: AbortSignal): Promise<{ me: SelfProfile }>;
  checkHandle(handle: string, signal?: AbortSignal): Promise<HandleCheck>;
  roomInvites(signal?: AbortSignal): Promise<{ invites: RoomInvitation[] }>;
  respondRoomInvite(id: string, accept: boolean, signal?: AbortSignal): Promise<{ accepted: boolean; roomId?: string }>;
  friends(signal?: AbortSignal): Promise<FriendsResponse>;
  searchFriends(q: string, signal?: AbortSignal): Promise<{ results: PublicProfile[] }>;
  presence(signal?: AbortSignal): Promise<{ friends: FriendPresence[]; serverTime: number }>;
  requestFriend(
    target: { handle: string } | { userId: UserId },
    signal?: AbortSignal
  ): Promise<{ requestId: string; accepted: boolean; to: PublicProfile }>;
  respondFriend(requestId: string, accept: boolean, signal?: AbortSignal): Promise<{ state: string }>;
  cancelRequest(requestId: string, signal?: AbortSignal): Promise<{ state: string }>;
  removeFriend(userId: UserId, signal?: AbortSignal): Promise<unknown>;
}

/**
 * The two functions this needs from the room socket, exactly as `chat/store.ts` takes
 * them. `subscribe` is handed EVERY inbound frame and this store picks out the ones it
 * knows, so the sync engine never has to learn what any of these are called.
 */
export interface SocialTransport {
  send(msg: SocialClientMessage): void;
  subscribe(fn: (msg: unknown) => void): () => void;
}

export interface SocialOptions {
  api?: SocialApiPort;
  now?: () => number;
  /** Injected in tests. Defaults to `setInterval`/`clearInterval`. */
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The snapshot
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `no-account` is NOT an error and not the same as `unavailable`.
 *  - `unavailable`: this build of the service has no social layer (404), or it cannot be
 *    reached. Nothing here can work and the screen should say so plainly.
 *  - `no-account`: reserved for the case where `GET /api/me` succeeded but returned no
 *    profile, which should be impossible; kept so an unexpected shape cannot render as
 *    "you have no friends".
 */
export type SocialPhase = 'idle' | 'loading' | 'ready' | 'no-account' | 'unavailable';

export type AskState = 'asking' | 'accepted' | 'declined' | 'expired' | 'no-room' | 'failed';

/** An ask this browser sent. Exactly one at a time — see `askToJoin`. */
export interface OutgoingAsk {
  to: PublicProfile;
  askedAt: number;
  expiresAt: number;
  state: AskState;
  /** Something to show a person. Always set once `state` leaves `asking`. */
  message: string | null;
  /** Where to go, on `accepted`. */
  roomId: string | null;
}

/** Somebody asking to come into YOUR room. */
export interface IncomingAsk {
  requestId: string;
  from: PublicProfile;
  roomId: string;
  expiresAt: number;
}

export interface SocialSnapshot {
  phase: SocialPhase;
  me: SelfProfile | null;
  /** False means the server forgets everybody on restart. Shown, not hidden. */
  durable: boolean;
  /** Server clock minus this browser's, at the last `me()`. Keeps the handle countdown
   *  honest on a device whose clock is wrong. */
  skewMs: number;

  friends: PublicProfile[];
  roomInvites: RoomInvitation[];
  inviteError: string | null;
  incoming: IncomingRequest[];
  outgoing: OutgoingRequest[];
  /** By userId. Only friends ever appear here. */
  presence: Record<UserId, FriendPresence>;

  outgoingAsk: OutgoingAsk | null;
  incomingAsks: IncomingAsk[];
  /** True when frames actually flow — i.e. asking to join can work at all. */
  live: boolean;

  /** Keys of in-flight actions, so one row can spin without freezing the screen.
   *  e.g. `profile`, `handle`, `friend:<userId>`, `request:<id>`. */
  busy: string[];
  /** The last thing that went wrong, in words a person can act on. */
  error: string | null;
  /** The last thing that went right and is worth confirming. */
  notice: string | null;
}

const EMPTY: SocialSnapshot = {
  phase: 'idle',
  me: null,
  durable: true,
  skewMs: 0,
  friends: [],
  roomInvites: [],
  inviteError: null,
  incoming: [],
  outgoing: [],
  presence: {},
  outgoingAsk: null,
  incomingAsks: [],
  live: false,
  busy: [],
  error: null,
  notice: null
};

/* ────────────────────────────────────────────────────────────────────────────
 * The store
 * ──────────────────────────────────────────────────────────────────────────── */

function message(err: unknown): string {
  const e = err as { message?: string } | null;
  return typeof e?.message === 'string' && e.message !== '' ? e.message : 'Something went wrong.';
}

function statusOf(err: unknown): number {
  const e = err as { status?: number } | null;
  return typeof e?.status === 'number' ? e.status : -1;
}

export class SocialStore {
  private snap: SocialSnapshot = EMPTY;
  private readonly listeners = new Set<(s: SocialSnapshot) => void>();

  private readonly api: SocialApiPort;
  private readonly clock: () => number;
  private readonly every: (fn: () => void, ms: number) => unknown;
  private readonly stopTimer: (handle: unknown) => void;

  private transport: SocialTransport | null = null;
  private offTransport: (() => void) | null = null;
  private poll: unknown = null;
  private watchers = 0;
  private askSentAt = 0;
  /** When a `friendPresence` push last landed. Evidence, not configuration: it is what
   *  decides whether the REST poll has anything to add. 0 means "never". */
  private lastPushAt = 0;
  private presenceRequest = 0;
  private presenceRevision = 0;
  private friendsRequest = 0;
  private inviteRevision = 0;
  private inviteRequest = 0;

  constructor(opts: SocialOptions = {}) {
    this.api = opts.api ?? (rest as unknown as SocialApiPort);
    this.clock = opts.now ?? Date.now;
    this.every = opts.setInterval ?? ((fn, ms) => setInterval(fn, ms));
    this.stopTimer = opts.clearInterval ?? ((h) => clearInterval(h as ReturnType<typeof setInterval>));
  }

  get snapshot(): SocialSnapshot {
    return this.snap;
  }

  /** Svelte-store shape, so a component can write `$social`. Fires immediately. */
  subscribe(fn: (s: SocialSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snap);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<SocialSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const fn of this.listeners) fn(this.snap);
  }

  private start(key: string): void {
    if (!this.snap.busy.includes(key)) this.set({ busy: [...this.snap.busy, key] });
  }

  private done(key: string): void {
    this.set({ busy: this.snap.busy.filter((k) => k !== key) });
  }

  /** Clear whatever the last action said. Called when a screen mounts, so yesterday's
   *  error is not the first thing anybody reads. */
  dismiss(): void {
    if (this.snap.error !== null || this.snap.notice !== null) this.set({ error: null, notice: null });
  }

  /* ---------- identity ---------- */

  /**
   * Who am I, and who are my friends. Idempotent and safe to call from every screen:
   * a second call while the first is in flight is a no-op.
   */
  async load(force = false): Promise<void> {
    if (this.snap.busy.includes('load')) return;
    if (!force && this.snap.phase === 'ready') {
      void this.refreshFriends();
      void this.refreshInvites();
      return;
    }
    this.start('load');
    if (this.snap.phase === 'idle') this.set({ phase: 'loading' });
    try {
      const body = await this.api.me();
      if (body?.me === undefined || body.me === null) {
        this.set({ phase: 'no-account' });
        return;
      }
      this.set({
        phase: 'ready',
        me: body.me,
        durable: body.durable !== false,
        skewMs: typeof body.serverTime === 'number' ? body.serverTime - this.clock() : 0
      });
      await this.refreshFriends();
      await this.refreshInvites();
    } catch (err) {
      // A 404 is a service with no social layer mounted; anything else is a service we
      // cannot reach. Both are "nothing here can work", and neither is the user's fault.
      this.set({ phase: 'unavailable', error: statusOf(err) === 404 ? null : message(err) });
    } finally {
      this.done('load');
    }
  }

  async saveProfile(patch: { displayName?: string; avatar?: AvatarId; privateMode?: boolean }): Promise<boolean> {
    if (this.snap.busy.includes('profile')) return false;
    this.start('profile');
    try {
      const body = await this.api.setProfile(patch);
      this.set({ me: body.me, error: null, notice: 'Saved.' });
      return true;
    } catch (err) {
      this.set({ error: message(err) });
      return false;
    } finally {
      this.done('profile');
    }
  }

  /**
   * Change the handle. The once-a-month rule is the SERVER'S, and a refusal is a `429`
   * carrying exactly how long is left — which is put straight into `me.handleChangeInMs`
   * so the screen counts down against the same number the server is enforcing rather
   * than one it worked out itself. Two owners of one number is a bug this project has
   * already paid for twice.
   */
  async changeHandle(raw: string): Promise<boolean> {
    const wanted = normaliseHandle(raw);
    if (wanted === null) {
      this.set({ error: 'A handle is 3–24 characters: letters, digits and single hyphens.' });
      return false;
    }
    if (this.snap.me !== null && wanted === this.snap.me.handle) {
      this.set({ notice: 'That is already your handle.' });
      return true;
    }
    this.start('handle');
    try {
      const body = await this.api.setHandle(wanted);
      this.set({ me: body.me, error: null, notice: `You are now @${body.me.handle}.` });
      return true;
    } catch (err) {
      const retry = (err as { retryAfterMs?: number }).retryAfterMs;
      if (typeof retry === 'number' && this.snap.me !== null) {
        // Adopt the server's remainder rather than keeping our stale zero.
        this.set({ me: { ...this.snap.me, handleChangeInMs: retry } });
      }
      this.set({ error: message(err) });
      return false;
    } finally {
      this.done('handle');
    }
  }

  /** Is this handle free? Never throws — an unreachable server is "we do not know". */
  async checkHandle(raw: string, signal?: AbortSignal): Promise<HandleCheck | null> {
    const wanted = normaliseHandle(raw);
    if (wanted === null) return { available: false, handle: null, code: 'bad-handle', message: 'Too short.' };
    try {
      return await this.api.checkHandle(wanted, signal);
    } catch {
      return null;
    }
  }

  async refreshInvites(): Promise<void> {
    const request = ++this.inviteRequest;
    const revision = this.inviteRevision;
    try {
      const body = await this.api.roomInvites(AbortSignal.timeout(15_000));
      if (request !== this.inviteRequest || revision !== this.inviteRevision) return;
      this.set({ roomInvites: body.invites ?? [], inviteError: null });
    } catch (err) {
      if (request === this.inviteRequest && revision === this.inviteRevision)
        this.set({ inviteError: inviteError(err) });
    }
  }

  async respondInvite(id: string, accept: boolean): Promise<string | null> {
    const key = `invite:${id}`;
    if (this.snap.busy.includes(key)) return null;
    this.start(key);
    this.set({ inviteError: null });
    try {
      const result = await this.api.respondRoomInvite(id, accept, AbortSignal.timeout(15_000));
      if (accept && (!result.accepted || !result.roomId)) {
        this.set({ inviteError: 'The invitation could not be accepted. Ask the owner for a new one.' });
        return null;
      }
      this.inviteRevision += 1;
      this.set({ roomInvites: this.snap.roomInvites.filter(i => i.id !== id) });
      return accept ? result.roomId! : null;
    } catch (err) {
      this.set({ inviteError: inviteError(err) });
      return null;
    } finally {
      this.done(key);
    }
  }

  /* ---------- friends ---------- */

  private async refreshFriends(): Promise<void> {
    const request = ++this.friendsRequest;
    try {
      const body = await this.api.friends();
      if (request !== this.friendsRequest) return;
      this.set({
        friends: body.friends ?? [],
        incoming: body.incoming ?? [],
        outgoing: body.outgoing ?? []
      });
    } catch (err) {
      // A friends list that failed to load is not a reason to blank the screen; keep
      // whatever we had and say so.
      if (statusOf(err) !== 401) this.set({ error: message(err) });
    }
  }

  async search(q: string, signal?: AbortSignal): Promise<PublicProfile[]> {
    const query = q.trim().replace(/^@+/, '');
    if (query.length < 2) return [];
    try {
      const body = await this.api.searchFriends(query, signal);
      return body.results ?? [];
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return [];
      this.set({ error: message(err) });
      return [];
    }
  }

  /**
   * Ask somebody to be friends.
   *
   * Three shortcuts the server takes and the UI has to be ready for: already friends
   * succeeds, asking twice is the same request, and asking somebody who has already
   * asked YOU is a yes — which comes back as `accepted: true` and means the friends list
   * just grew rather than the outbox.
   */
  async addFriend(target: { handle: string } | { userId: UserId }): Promise<boolean> {
    const key = 'userId' in target ? `friend:${target.userId}` : `friend:${target.handle}`;
    this.start(key);
    try {
      const body = await this.api.requestFriend(target);
      await this.refreshFriends();
      this.set({
        error: null,
        notice: body.accepted
          ? `You and ${personName(body.to)} are friends.`
          : `Asked ${personName(body.to)}. They will see it next time they open Jovii.`
      });
      return true;
    } catch (err) {
      this.set({ error: message(err) });
      return false;
    } finally {
      this.done(key);
    }
  }

  async respond(requestId: string, accept: boolean): Promise<boolean> {
    const key = `request:${requestId}`;
    const asking = this.snap.incoming.find((r) => r.id === requestId);
    this.start(key);
    try {
      await this.api.respondFriend(requestId, accept);
      await this.refreshFriends();
      this.set({
        error: null,
        notice:
          asking === undefined
            ? null
            : accept
              ? `You and ${personName(asking.from)} are friends.`
              : `Declined. ${personName(asking.from)} is not told.`
      });
      return true;
    } catch (err) {
      // A request answered on another device is gone, not broken. Re-read and move on.
      await this.refreshFriends();
      this.set({ error: statusOf(err) === 404 ? 'That request is no longer open.' : message(err) });
      return false;
    } finally {
      this.done(key);
    }
  }

  async cancel(requestId: string): Promise<boolean> {
    const key = `request:${requestId}`;
    this.start(key);
    try {
      await this.api.cancelRequest(requestId);
      await this.refreshFriends();
      this.set({ error: null, notice: 'Withdrawn.' });
      return true;
    } catch (err) {
      await this.refreshFriends();
      this.set({ error: message(err) });
      return false;
    } finally {
      this.done(key);
    }
  }

  async unfriend(userId: UserId): Promise<boolean> {
    const key = `friend:${userId}`;
    const who = this.snap.friends.find((f) => f.userId === userId);
    this.start(key);
    try {
      await this.api.removeFriend(userId);
      await this.refreshFriends();
      const presence = { ...this.snap.presence };
      delete presence[userId];
      this.set({ presence, error: null, notice: who === undefined ? null : `Removed ${personName(who)}.` });
      return true;
    } catch (err) {
      this.set({ error: message(err) });
      return false;
    } finally {
      this.done(key);
    }
  }

  /* ---------- presence ---------- */

  /**
   * Keep `presence` fresh while a screen is looking at it. Returns a stop function;
   * nested calls are counted, so two screens can watch and the last one to leave stops
   * the work.
   *
   * With a live transport this is one `friendsWatch` and then nothing at all. Without
   * one it is a poll — see the file header for why it is slow and why it stops when the
   * page is hidden.
   */
  watchPresence(): () => void {
    this.watchers += 1;
    if (this.watchers === 1) this.beginWatching();
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      this.watchers -= 1;
      if (this.watchers === 0) this.endWatching();
    };
  }

  /**
   * A TRANSPORT IS NOT A DELIVERY. This used to ask for the push and RETURN — so if the
   * frame went nowhere, nothing else ever asked, and the Friends screen was a snapshot
   * taken at page load. Measured on the deployed site: a friend entering a room was
   * visible to `GET /api/friends/presence` in 13 ms and the screen had not noticed after
   * 90 seconds, with zero requests in the window and `presenceWatchers: 0` on the server
   * for the whole session. It still offered "Ask to join" for a room somebody had left.
   *
   * The reason is that these screens are not in a room. `attachSocial()` hands over the
   * tab's sync engine whether or not it has an open, joined socket, and `sendRaw` drops
   * silently when it does not — so `transport !== null` meant "there is an object here",
   * never "the server heard me".
   *
   * So both are armed, and the PUSH SUPPRESSES THE POLL rather than replacing it: a
   * working socket costs one REST read at mount and nothing after, a socket that is not
   * listening costs a small JSON every 12 s, and neither case can go stale. The store
   * learns which world it is in from frames actually arriving, which is the only
   * evidence that means anything.
   */
  private beginWatching(): void {
    this.transport?.send({ t: 'friendsWatch', on: true });
    void this.refreshPresence();
    this.poll = this.every(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void this.refreshFriends();
      void this.refreshInvites();
      // A push landed recently enough that a poll would only be asking a question we
      // already have a fresher answer to.
      if (this.lastPushAt !== 0 && this.clock() - this.lastPushAt < PRESENCE_POLL_MS * 2) return;
      void this.refreshPresence();
    }, PRESENCE_POLL_MS);
  }

  private endWatching(): void {
    this.transport?.send({ t: 'friendsWatch', on: false });
    if (this.poll !== null) {
      this.stopTimer(this.poll);
      this.poll = null;
    }
  }

  /** One REST read of what everybody is watching. Silent on failure: presence is a
   *  nicety, and an error banner over a friends list because a poll missed is noise. */
  async refreshPresence(): Promise<void> {
    const request = ++this.presenceRequest;
    const revision = this.presenceRevision;
    try {
      const body = await this.api.presence();
      // A push or a newer poll supersedes this read, even within the same ms.
      if (request !== this.presenceRequest || revision !== this.presenceRevision) return;
      this.applyPresence(body.friends ?? []);
    } catch {
      /* deliberately quiet */
    }
  }

  private applyPresence(list: FriendPresence[]): void {
    this.presenceRevision += 1;
    const presence: Record<UserId, FriendPresence> = {};
    for (const p of list) presence[p.userId] = p;
    this.set({ presence });
    const ids = new Set(this.snap.friends.map(f => f.userId));
    if (list.length !== ids.size || list.some(p => !ids.has(p.userId))) void this.refreshFriends();
  }

  /* ---------- the room socket ---------- */

  /**
   * Hand over the tab's one room socket, or `null` to take it away.
   *
   * Everything above works without it. Only asking to join needs it, because there is no
   * REST form of a question that has to appear on somebody else's screen.
   */
  connect(transport: SocialTransport | null): void {
    if (this.transport === transport) return;
    const wasWatching = this.watchers > 0;
    if (wasWatching) this.endWatching();
    this.offTransport?.();
    this.offTransport = null;
    this.transport = transport;
    if (transport !== null) this.offTransport = transport.subscribe((msg) => this.receive(msg));
    this.set({ live: transport !== null });
    if (wasWatching) this.beginWatching();
  }

  /** Every inbound frame on the room socket. Unknown types are ignored, which is what
   *  lets this share a socket with sync, calls and chat. */
  receive(raw: unknown): void {
    if (typeof raw !== 'object' || raw === null) return;
    const msg = raw as { t?: unknown };
    switch (msg.t) {
      case 'welcome': {
        // sendRaw drops before open, so subscribe again on every handshake.
        if (this.watchers > 0) this.transport?.send({ t: 'friendsWatch', on: true });
        return;
      }
      case 'roomInvites': {
        const frame = raw as { invites?: RoomInvitation[] };
        if (Array.isArray(frame.invites)) {
          this.inviteRevision += 1;
          this.set({ roomInvites: frame.invites, inviteError: null });
        }
        return;
      }
      case 'friendPresence': {
        const frame = raw as { friends?: FriendPresence[] };
        if (Array.isArray(frame.friends)) {
          this.lastPushAt = this.clock();
          this.applyPresence(frame.friends);
        }
        return;
      }
      case 'joinRequest': {
        const frame = raw as { requestId?: string; from?: PublicProfile; roomId?: string; expiresAt?: number };
        if (typeof frame.requestId !== 'string' || frame.from === undefined || typeof frame.roomId !== 'string') return;
        // Every live socket of theirs gets the prompt, so the same request can arrive
        // twice on one device across a reconnect. Keyed, never appended blindly.
        const rest = this.snap.incomingAsks.filter((a) => a.requestId !== frame.requestId);
        this.set({
          incomingAsks: [
            ...rest,
            {
              requestId: frame.requestId,
              from: frame.from,
              roomId: frame.roomId,
              expiresAt: frame.expiresAt ?? this.clock() + JOIN_REQUEST_TTL_MS
            }
          ]
        });
        return;
      }
      case 'joinAnswered': {
        this.settleAsk(raw as { accepted?: boolean; roomId?: string; by?: PublicProfile; reason?: string });
        return;
      }
      case 'error': {
        // The server does NOT acknowledge `askToJoin`, so a refusal arrives as a plain
        // `error` frame with nothing tying it to the ask. Correlation is therefore by
        // time and by code, and only while an ask is actually outstanding. See the
        // report: an ack carrying the requestId would make this exact.
        const frame = raw as { code?: string; message?: string };
        const ask = this.snap.outgoingAsk;
        if (ask === null || ask.state !== 'asking') return;
        if (this.clock() - this.askSentAt > ASK_ERROR_WINDOW_MS) return;
        if (frame.code !== 'no-room' && frame.code !== 'rate-limited' && frame.code !== 'bad-message') return;
        this.set({
          outgoingAsk: {
            ...ask,
            state: frame.code === 'no-room' ? 'no-room' : 'failed',
            message: frame.message ?? 'They could not be asked.'
          }
        });
        return;
      }
      default:
        return;
    }
  }

  /* ---------- asking to join ---------- */

  /**
   * Ask a friend to let you into the room they are in.
   *
   * ONE AT A TIME, on purpose. The server refuses a second ask to the same person and
   * gives the asker no id to match answers against, so more than one in flight would
   * mean guessing which answer belonged to which question. One is also what a person
   * does.
   */
  askToJoin(friend: PublicProfile): void {
    if (this.transport === null) {
      this.set({
        outgoingAsk: {
          to: friend,
          askedAt: this.clock(),
          expiresAt: this.clock(),
          state: 'failed',
          message: 'Asking needs the room connection, which this build does not have yet.',
          roomId: null
        }
      });
      return;
    }
    const at = this.clock();
    this.askSentAt = at;
    this.set({
      error: null,
      outgoingAsk: {
        to: friend,
        askedAt: at,
        expiresAt: at + JOIN_REQUEST_TTL_MS,
        state: 'asking',
        message: null,
        roomId: null
      }
    });
    this.transport.send({ t: 'askToJoin', to: friend.userId });
  }

  /** Stop waiting. The question is still on their screen until it expires — the server
   *  has no withdraw — so this is honest about only being OUR side of it. */
  forgetAsk(): void {
    if (this.snap.outgoingAsk !== null) this.set({ outgoingAsk: null });
  }

  /**
   * Nobody answered. Called by a screen's timer rather than by a timer of ours: the
   * server expires the request on its own tick and tells us, and a second countdown
   * here would be a second owner of the same moment. This is only the fallback for a
   * socket that went away while we waited.
   */
  expireAsk(): void {
    const ask = this.snap.outgoingAsk;
    if (ask === null || ask.state !== 'asking') return;
    if (this.clock() < ask.expiresAt) return;
    this.set({
      outgoingAsk: { ...ask, state: 'expired', message: `${personName(ask.to)} did not answer.` }
    });
  }

  private settleAsk(frame: { accepted?: boolean; roomId?: string; by?: PublicProfile; reason?: string }): void {
    const ask = this.snap.outgoingAsk;
    if (ask === null || ask.state !== 'asking') return;
    // When somebody answered, the frame says who; an expiry says nothing at all, and
    // with one ask outstanding that is enough to know which one it was.
    if (frame.by !== undefined && frame.by.userId !== ask.to.userId) return;

    if (frame.accepted === true && typeof frame.roomId === 'string') {
      this.set({
        outgoingAsk: {
          ...ask,
          state: 'accepted',
          roomId: frame.roomId,
          message: `${personName(ask.to)} let you in.`
        }
      });
      return;
    }
    const state: AskState = frame.reason === 'expired' ? 'expired' : frame.reason === 'no-room' ? 'no-room' : 'declined';
    this.set({
      outgoingAsk: {
        ...ask,
        state,
        message:
          state === 'expired'
            ? `${personName(ask.to)} did not answer.`
            : state === 'no-room'
              ? `${personName(ask.to)} is not in a room any more.`
              : `${personName(ask.to)} said no.`
      }
    });
  }

  /** Answer somebody who asked to come into your room. */
  answerAsk(requestId: string, accept: boolean): void {
    this.set({ incomingAsks: this.snap.incomingAsks.filter((a) => a.requestId !== requestId) });
    this.transport?.send({ t: 'joinAnswer', requestId, accept });
  }

  /** Drop prompts nobody answered. Driven by the screen's own tick. */
  expireIncoming(): void {
    const t = this.clock();
    const live = this.snap.incomingAsks.filter((a) => a.expiresAt > t);
    if (live.length !== this.snap.incomingAsks.length) this.set({ incomingAsks: live });
  }

  /* ---------- room history: NOT HERE ----------
   *
   * The server history route and the device-local room recorder are retired.
   * This store has no transcript or room-visit history port. Browser playback
   * progress remains a separate preferences feature; old recorder bytes are untouched.
   * -------------------------------------------- */

  /** Torn down by nobody today — the store is a tab singleton — but a bench page mounts
   *  and unmounts one, and a leaked interval on a bench is still a leak. */
  destroy(): void {
    if (this.poll !== null) {
      this.stopTimer(this.poll);
      this.poll = null;
    }
    this.offTransport?.();
    this.offTransport = null;
    this.transport = null;
    this.watchers = 0;
    this.listeners.clear();
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The tab's one store
 * ──────────────────────────────────────────────────────────────────────────── */

let singleton: SocialStore | null = null;

/** The tab's one social store. Created on first use; fetches nothing until `load()`. */
export function social(): SocialStore {
  singleton ??= new SocialStore();
  return singleton;
}
